// @flow strict

import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';

// Constants
import { NAVIGATION_ROUTES } from 'utils/constants';

// Services
import { fetchCircuit } from 'services/Circuit/thunks';
import {
    selectMinChemCircuitQuery,
    selectCircuitIsFetchingStatus,
} from 'services/Circuit/selectors';

// Microfrontend
import { isMinChemMfeVersion } from 'microfrontends/MinChemV2/versions';
import { MinChemMfeUrls } from 'microfrontends/MinChemV2/urls';

// Components
import CircuitVersionWarningModal from 'components/Modals/CircuitVersionWarningModal';
import AppLoader from 'components/AppLoader';

// Containers
import MimicDiagramContainer from './MimicDiagramContainer';

// Types
import type { HistoryType, ReduxDispatch, ImmutableQueryStructure } from 'types';
import type { ImmutableCircuit } from 'services/Circuit/types';

type Props = {
    circuitId: number,
    history: HistoryType,

    minChemCircuitQuery: ImmutableQueryStructure<ImmutableCircuit>,
    fetchingSingleCircuit: boolean,
    fetchCircuit: (id: number) => void,
};

type State = {
    showVersionWarning: boolean,
    userDismissedWarning: boolean,
};

/**
 * TODO: MS-531 - Merge CircuitSetupContainer & MimicDiagramContainer
 * This double container was partially removed when we went to a circuit elevate route
 */
class CircuitSetupContainer extends React.PureComponent<Props, State> {
    state = {
        showVersionWarning: false,
        userDismissedWarning: false,
    };

    componentDidMount() {
        const circuit = this.getCircuit();
        if (!circuit) {
            this.props.fetchCircuit(this.props.circuitId);
        } else {
            this.checkCircuitVersion(circuit);
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (!this.state.userDismissedWarning) {
            const circuit = this.getCircuit();
            const prevCircuit = prevProps.minChemCircuitQuery
                .get('data')
                .find((c: ImmutableCircuit) => c.get('id') === this.props.circuitId);
            if (circuit && !prevCircuit) {
                this.checkCircuitVersion(circuit);
            }
        }
    }

    getCircuit = (): ?ImmutableCircuit =>
        this.props.minChemCircuitQuery
            .get('data')
            .find((circuit: ImmutableCircuit) => circuit.get('id') === this.props.circuitId);

    checkCircuitVersion = (circuit: ImmutableCircuit) => {
        if (isMinChemMfeVersion(circuit.get('version'))) {
            this.setState({ showVersionWarning: true });
        }
    };

    handleNavigateToNewVersion = () => {
        this.props.history.replace(
            MinChemMfeUrls.getCircuitSetupUrl(this.props.circuitId)
        );
    };

    handleStayInOldVersion = () => {
        this.setState({ showVersionWarning: false, userDismissedWarning: true });
    };

    /**
     * When the user wants to elevate their circuit to a SolvExtract circuit.
     */
    handleElevateToSolvExtractCircuit = (circuitId: number) =>
        this.props.history.push(
            `${NAVIGATION_ROUTES.CIRCUIT}${circuitId}${NAVIGATION_ROUTES.ELEVATE}`
        );

    render() {
        const circuit = this.getCircuit();

        // Show version warning modal for v2 circuits
        if (this.state.showVersionWarning) {
            return (
                <CircuitVersionWarningModal
                    onNavigateToNewVersion={this.handleNavigateToNewVersion}
                    onStayInOldVersion={this.handleStayInOldVersion}
                />
            );
        }

        // Wait for circuit to load so we can check its version
        if (!circuit && this.props.fetchingSingleCircuit) {
            return (
                <AppLoader
                    messageId="containers.CircuitSetupContainer.loadingCircuit"
                    loading
                />
            );
        }

        return (
            <MimicDiagramContainer
                circuitId={this.props.circuitId}
                onElevateToSolvExtractCircuit={this.handleElevateToSolvExtractCircuit}
            />
        );
    }
}

const mapStateToProps = () =>
    createStructuredSelector({
        minChemCircuitQuery: selectMinChemCircuitQuery(),
        fetchingSingleCircuit: selectCircuitIsFetchingStatus(),
    });

const mapDispatchToProps = (dispatch: ReduxDispatch) =>
    bindActionCreators(
        {
            fetchCircuit,
        },
        dispatch
    );

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(CircuitSetupContainer)
);
